// HybridToleranceSlider — shown on the daily log when ArmIQ has auto-suggested
// a hybrid for the day. Lets the athlete flex one band up or down based on how
// the arm feels, instead of typing the hybrid out. The picked value is what
// actually gets logged; the component also exposes whether the athlete went
// up/down/stayed via the `delta` callback so coaches can see who's overriding
// the engine.
import { useMemo } from "react";

const ORDER = ["A", "B", "C", "D", "E"];
const COLORS = {
  A: "#D90429", B: "#F59E0B", C: "#3B82F6", D: "#22D3EE", E: "#16A34A",
};
const NAMES = {
  A: "Long Toss + Pull-Downs",
  B: "Long Toss — No Pull-Downs",
  C: "Short Long Toss 90/120 ft",
  D: "ROM + Light Toss",
  E: "ROM Throws Only",
};
const FEEL = {
  "-1": { label: "Feels great — step up", color: "#10B981" },
   "0": { label: "As suggested", color: "var(--brand-secondary)" },
   "1": { label: "Still tight — step down", color: "#F59E0B" },
};

export default function HybridToleranceSlider({
  suggestedLetter,    // letter from readiness engine, e.g. "C"
  currentLetter,      // currently-selected letter (driven by parent state)
  onChange,           // (newLetter, deltaFromSuggested) => void
  testIdPrefix = "tolerance",
}) {
  const idx = ORDER.indexOf(suggestedLetter);
  // Valid bands: one above + suggested + one below (capped at A and E)
  const options = useMemo(() => {
    if (idx < 0) return [];
    const result = [];
    if (idx > 0) result.push({ letter: ORDER[idx - 1], delta: -1 });
    result.push({ letter: ORDER[idx], delta: 0 });
    if (idx < ORDER.length - 1) result.push({ letter: ORDER[idx + 1], delta: 1 });
    return result;
  }, [idx]);

  if (!options.length) return null;

  const selectedDelta = options.find((o) => o.letter === currentLetter)?.delta ?? 0;
  const selectedColor = FEEL[String(selectedDelta)]?.color || "var(--brand-secondary)";

  return (
    <div className="rounded-lg p-3"
         style={{ backgroundColor: "var(--ink-1)", border: "1px solid var(--ink-border)" }}
         data-testid={`${testIdPrefix}-slider`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">
          How does the arm feel?
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em]" style={{ color: selectedColor }}>
          {FEEL[String(selectedDelta)]?.label}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {options.map(({ letter, delta }) => {
          const isActive = letter === currentLetter;
          const c = COLORS[letter] || "#888";
          return (
            <button
              key={letter}
              type="button"
              onClick={() => onChange?.(letter, delta)}
              data-testid={`${testIdPrefix}-${letter}`}
              className="rounded-lg p-2.5 text-left transition"
              style={{
                backgroundColor: isActive ? `${c}22` : "transparent",
                border: `1px solid ${isActive ? c : "rgba(255,255,255,0.1)"}`,
                color: isActive ? "white" : "rgba(255,255,255,0.7)",
              }}
            >
              <div className="flex items-baseline gap-1.5">
                <span className="font-display font-black text-xl" style={{ color: c }}>{letter}</span>
                {delta === 0 && (
                  <span className="text-[9px] font-extrabold tracking-[0.18em] uppercase rounded px-1"
                        style={{ color: c, border: `1px solid ${c}` }}>
                    Suggested
                  </span>
                )}
              </div>
              <div className="text-[10px] text-white/60 mt-1 leading-tight">{NAMES[letter]}</div>
            </button>
          );
        })}
      </div>

      <p className="text-[10px] text-white/40 mt-2 leading-tight">
        Tap a band to flex up (feeling great) or down (tight / sore). Your coach sees the engine's pick + your choice side-by-side.
      </p>
    </div>
  );
}
